import React, { useEffect } from "react";
import {
  Mic, Phone, PhoneIncoming, FileText, DollarSign, Play, ArrowRight,
  CalendarClock, Receipt, UsersRound, LayoutGrid, Repeat2, PhoneCall,
  Handshake, BarChart3, ShieldCheck, CreditCard, Bot, MapPin, CheckCircle2,
  User, Building2, Eye, Sparkles, Menu, Clock, Navigation, Calendar,
  Users, Wallet, Search, Bell, Plus, Minus, Compass,
} from "lucide-react";

const FONT_LINK = "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&family=Outfit:wght@500;600;700;800;900&display=swap";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1625148230889-8195e85aae6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxmaWVsZCUyMHNlcnZpY2UlMjB0ZWNobmljaWFuJTIwdmFuJTIwcGhvbmV8ZW58MHx8fHwxNzg3NzUyODc2fDA&ixlib=rb-4.1.0&q=85",
  voice: "https://images.pexels.com/photos/6471913/pexels-photo-6471913.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  office: "https://images.pexels.com/photos/4267524/pexels-photo-4267524.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  field: "https://images.pexels.com/photos/38136632/pexels-photo-38136632.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  finalCta: "https://images.pexels.com/photos/5366406/pexels-photo-5366406.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

function useGoogleFonts() {
  useEffect(() => {
    if (document.getElementById("mp-fonts")) return;
    const link = document.createElement("link");
    link.id = "mp-fonts";
    link.rel = "stylesheet";
    link.href = FONT_LINK;
    document.head.appendChild(link);
  }, []);
}

function PreviewBanner() {
  return (
    <div className="sticky top-0 z-[60] flex items-center justify-center gap-2 bg-amber-500 px-4 py-2 text-center text-[12px] font-bold uppercase tracking-wide text-slate-950" data-testid="mp-preview-banner">
      <Eye className="h-3.5 w-3.5" />
      Design Preview — static mockup, not connected to any backend, database, or authentication
    </div>
  );
}

function Nav() {
  return (
    <header className="sticky top-[34px] z-50 border-b border-white/10 bg-slate-950/75 backdrop-blur-xl" data-testid="mp-nav">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
        <div className="flex items-center gap-2" data-testid="mp-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-800 text-white">
            <Sparkles className="h-5 w-5" strokeWidth={2.5} />
          </div>
          <span className="font-outfit text-[18px] font-bold text-white">ServiceGuru <span className="text-blue-400">Pro</span></span>
        </div>
        <nav className="hidden items-center gap-8 md:flex">
          {["Features", "AI Assistant", "Pricing"].map((l) => (
            <a key={l} href="#" data-testid={`mp-nav-${l.toLowerCase().replace(/\s/g, "-")}`} className="text-[14px] font-medium text-slate-300 transition-colors hover:text-white">{l}</a>
          ))}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <button data-testid="mp-nav-login-btn" className="text-[14px] font-semibold text-slate-300 transition-colors hover:text-white">Log In</button>
          <button data-testid="mp-nav-cta-btn" className="rounded-full bg-blue-600 px-5 py-2.5 text-[14px] font-bold text-white transition-all hover:bg-blue-700 active:scale-95">
            Start Free Trial
          </button>
        </div>
        <button data-testid="mp-nav-mobile-menu-btn" className="text-slate-300 md:hidden"><Menu className="h-6 w-6" /></button>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-slate-950 pt-20 pb-24 sm:pt-28 sm:pb-32" data-testid="mp-hero-section">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-blue-600/10 blur-[120px]" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-10 lg:px-8">
        <div>
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3.5 py-1.5 text-[12px] font-semibold uppercase tracking-wide text-amber-400">
            <Bot className="h-3.5 w-3.5" /> AI-Powered Field Service CRM
          </div>
          <h1 className="font-outfit text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="mp-hero-headline">
            Run Your Business <span className="text-blue-400">Hands-Free</span> With AI.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg" data-testid="mp-hero-subheadline">
            ServiceGuru Pro combines a powerful CRM with an AI voice assistant that schedules jobs, follows up with customers, and keeps your business moving — all without lifting a finger.
          </p>
          <div className="mt-9 flex flex-col gap-3.5 sm:flex-row">
            <button data-testid="mp-hero-primary-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-[15px] font-bold text-white transition-all hover:bg-blue-700 active:scale-95">
              Start Free Trial <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="mp-hero-secondary-cta" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 text-[15px] font-bold text-white transition-all hover:bg-white/10 active:scale-95">
              <Play className="h-4 w-4 fill-current" /> Watch Demo
            </button>
          </div>
          <div className="mt-10 flex items-center gap-6 text-[13px] text-slate-500">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-blue-400" /> 14-day free trial</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-blue-400" /> No credit card</span>
          </div>
        </div>
        <div className="relative">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/50">
            <img
              src={IMAGES.hero}
              alt="Field service technician in work gear speaking on the phone next to a work van"
              className="h-[420px] w-full object-cover sm:h-[480px]"
              data-testid="mp-hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/0 to-slate-950/10" />
          </div>
          <div className="absolute -bottom-6 -left-6 flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/90 px-4 py-3 shadow-xl backdrop-blur-xl sm:-left-10">
            <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-amber-500">
              <Mic className="h-5 w-5 text-slate-950" />
              <span className="absolute -inset-1 animate-ping rounded-full border border-amber-400/60" />
            </div>
            <div>
              <div className="text-[12px] font-bold text-white">Guru is listening…</div>
              <div className="text-[11px] text-slate-400">"Schedule a job for tomorrow at 2pm"</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function VoiceAssistantSection() {
  const bullets = [
    "Create jobs, tasks, and calendar events by voice",
    "Search customers hands-free, mid-job",
    "Get business answers just by asking",
  ];
  return (
    <section className="bg-slate-950 py-24 sm:py-32" data-testid="mp-voice-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div className="relative order-2 lg:order-1">
          <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/50">
            <img src={IMAGES.voice} alt="Technician's hands working on equipment with a phone nearby" className="h-[380px] w-full object-cover sm:h-[440px]" data-testid="mp-voice-image" />
          </div>
          <div className="absolute right-6 top-6 flex items-center gap-2 rounded-full border border-amber-400/30 bg-slate-950/80 px-4 py-2.5 shadow-lg backdrop-blur-xl">
            <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/20">
              <Mic className="h-4 w-4 text-amber-400" />
              <span className="absolute -inset-1.5 animate-ping rounded-full bg-amber-400/20" />
            </span>
            <span className="text-[12px] font-bold text-amber-300">Voice Active</span>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <h2 className="font-outfit text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="mp-voice-headline">
            Your CRM Listens, So You Don't Have To Type.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-400 sm:text-lg">
            Talk to Guru like a teammate. Create records, pull up customer history, and get straight answers about your business — hands never leave the tool.
          </p>
          <ul className="mt-7 space-y-3.5">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 text-[15px] text-slate-200">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500/15">
                  <CheckCircle2 className="h-3.5 w-3.5 text-blue-400" />
                </span>
                {b}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function FleetMapMock() {
  const pins = [
    { x: 8, y: 78 }, { x: 22, y: 40 }, { x: 46, y: 40 }, { x: 46, y: 14 },
    { x: 74, y: 14 }, { x: 90, y: 52 }, { x: 61, y: 84 },
  ];
  const tech = { x: 74, y: 52 };
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-3 shadow-2xl shadow-black/40" data-testid="mp-gps-mock">
      <div className="mb-2.5 flex items-center justify-between px-1.5">
        <span className="flex items-center gap-1.5 text-[12px] font-bold text-white"><Compass className="h-3.5 w-3.5 text-blue-400" /> FleetSync</span>
        <div className="flex items-center gap-2.5 text-slate-400">
          <Search className="h-3.5 w-3.5" />
          <Bell className="h-3.5 w-3.5" />
          <span className="flex items-center gap-1 rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-bold text-blue-300"><span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> Live Map</span>
        </div>
      </div>
      <div className="relative h-[300px] overflow-hidden rounded-2xl bg-slate-950 sm:h-[340px]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)", backgroundSize: "28px 28px" }}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
          <polyline points="8,78 22,78 22,40 46,40 46,14 74,14 74,52" fill="none" stroke="#60A5FA" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <polyline points="90,52 74,52" fill="none" stroke="#60A5FA" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <polyline points="74,52 61,84" fill="none" stroke="#F59E0B" strokeWidth="1.3" vectorEffect="non-scaling-stroke" />
        </svg>
        {pins.map((p, i) => (
          <span key={i} className="absolute -translate-x-1/2 -translate-y-1/2 text-white drop-shadow" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
            <MapPin className="h-4 w-4 fill-white text-slate-800" />
          </span>
        ))}
        <span className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${tech.x}%`, top: `${tech.y}%` }} data-testid="mp-gps-tech-marker">
          <span className="relative flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-amber-500 shadow-lg">
            <User className="h-3 w-3 text-slate-950" />
            <span className="absolute -inset-2 animate-ping rounded-full border border-amber-400/60" />
          </span>
        </span>
        <div className="absolute right-3 top-3 w-[168px] rounded-lg border border-white/10 bg-slate-900/95 p-3 shadow-xl backdrop-blur-xl sm:w-[190px]" data-testid="mp-gps-info-card">
          <div className="flex items-center justify-between text-[10px] font-bold text-slate-300">
            <span>TECH Sarah J.</span>
            <span className="flex items-center gap-1 text-emerald-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Online</span>
          </div>
          <span className="mt-1.5 inline-block rounded bg-amber-500/20 px-1.5 py-0.5 text-[9.5px] font-bold text-amber-300">En route</span>
          <div className="mt-1.5 text-[11.5px] font-bold text-white">Repair — 145 Maple Blvd</div>
          <div className="mt-1 text-[10px] text-slate-400">ETA 16 min · 1.2 mi · 38 mph</div>
          <div className="mt-2 flex items-center gap-1.5 border-t border-white/10 pt-2 text-[10px] text-slate-400">
            <Navigation className="h-3 w-3" /> Ford Transit #104
          </div>
        </div>
        <div className="absolute bottom-3 right-3 flex flex-col gap-1">
          <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-800 text-slate-300"><Plus className="h-3 w-3" /></span>
          <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-800 text-slate-300"><Minus className="h-3 w-3" /></span>
        </div>
      </div>
    </div>
  );
}

function GPSTrackingSection() {
  const features = [
    { icon: MapPin, title: "Live technician locations", desc: "Watch your crew move across the dispatch map in real time." },
    { icon: Clock, title: "Geo-aware arrival logs", desc: "Automatic on-site and departure timestamps for every job." },
    { icon: Navigation, title: "Smart routing & ETAs", desc: "Cut windshield time and keep customers informed with live ETAs." },
  ];
  return (
    <section className="bg-slate-950 py-24 sm:py-32" data-testid="mp-gps-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div>
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3.5 py-1.5 text-[12px] font-semibold uppercase tracking-wide text-amber-400">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Live GPS Tracking
          </div>
          <h2 className="font-outfit text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="mp-gps-headline">
            Know where every field tech is — in real time
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-400 sm:text-lg" data-testid="mp-gps-subheadline">
            Track your whole fleet live on the map, log geo-aware arrivals and departures automatically, and send customers accurate ETAs the moment a tech is on the way.
          </p>
          <div className="mt-8 space-y-5">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-3.5" data-testid={`mp-gps-feature-${f.title.split(" ")[0].toLowerCase()}`}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/15">
                  <f.icon className="h-5 w-5 text-blue-400" />
                </span>
                <div>
                  <div className="text-[15px] font-bold text-white">{f.title}</div>
                  <div className="mt-0.5 text-[13.5px] text-slate-400">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-9 flex flex-col gap-3.5 sm:flex-row">
            <button data-testid="mp-gps-primary-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-[14.5px] font-bold text-white transition-all hover:bg-blue-700 active:scale-95">
              <Calendar className="h-4 w-4" /> See it in a demo
            </button>
            <button data-testid="mp-gps-secondary-cta" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-[14.5px] font-bold text-white transition-all hover:bg-white/10 active:scale-95">
              Start free trial
            </button>
          </div>
        </div>
        <FleetMapMock />
      </div>
    </section>
  );
}

function Waveform() {
  const bars = [6, 12, 20, 14, 24, 10, 18, 8];
  return (
    <div className="flex items-center gap-[3px]" data-testid="mp-waveform">
      {bars.map((h, i) => (
        <span key={i} className="w-[3px] animate-pulse rounded-full bg-blue-400" style={{ height: `${h}px`, animationDelay: `${i * 90}ms` }} />
      ))}
    </div>
  );
}

function OfficeSection() {
  const checklist = [
    "Drag-and-drop dispatch board",
    "AI follow-up calls that collect payment",
    "Real-time job status from the field",
    "Estimates, invoices & online payments",
  ];
  return (
    <section className="bg-white py-24 sm:py-32" data-testid="mp-office-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div className="relative">
          <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/60">
            <img src={IMAGES.office} alt="Dispatcher with headset coordinating field technicians from the office" className="h-[360px] w-full object-cover sm:h-[420px]" data-testid="mp-office-image" />
          </div>
          <div className="absolute -bottom-6 -right-4 w-52 rounded-xl border border-slate-800 bg-slate-900 p-3.5 shadow-xl sm:-right-8" data-testid="mp-office-call-card">
            <div className="flex items-center justify-between">
              <Waveform />
              <Phone className="h-3.5 w-3.5 text-blue-400" />
            </div>
            <div className="mt-2 text-[12px] font-bold text-white">Sarah Jenkins</div>
            <div className="text-[10.5px] text-slate-400">Incoming call…</div>
          </div>
        </div>
        <div>
          <div className="text-[12px] font-bold uppercase tracking-wide text-blue-600" data-testid="mp-office-eyebrow">For the Office</div>
          <h2 className="mt-2 font-outfit text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="mp-office-headline">
            A command center for your whole operation
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="mp-office-subheadline">
            Dispatch the right tech, watch jobs progress live, and let the AI Voice Agent follow up on every open estimate — sending invoices and pay links automatically.
          </p>
          <ul className="mt-7 space-y-3">
            {checklist.map((c) => (
              <li key={c} className="flex items-center gap-3 text-[15px] text-slate-700" data-testid={`mp-office-check-${c.split(" ")[0].toLowerCase()}`}>
                <CheckCircle2 className="h-4.5 w-4.5 shrink-0 text-blue-600" />
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function PhoneMock() {
  return (
    <div className="w-28 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl" data-testid="mp-field-phone-mock">
      <div className="space-y-1.5 rounded-lg bg-slate-800 p-2.5">
        <div className="h-1.5 w-3/5 rounded-full bg-slate-600" />
        <div className="h-1.5 w-4/5 rounded-full bg-slate-600" />
        <div className="h-1.5 w-2/5 rounded-full bg-slate-600" />
        <div className="mt-2 rounded-full bg-blue-500 py-1.5 text-center text-[7px] font-bold text-white">COLLECT PAYMENT</div>
      </div>
    </div>
  );
}

function FieldSection() {
  const checklist = [
    "Work orders, checklists & photos",
    "One-tap GPS check-in & check-out",
    "Collect card payment on site",
    "Guru AI checklists for every job type",
  ];
  return (
    <section className="bg-slate-50 py-24 sm:py-32" data-testid="mp-field-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div className="order-2 lg:order-1">
          <div className="text-[12px] font-bold uppercase tracking-wide text-blue-600" data-testid="mp-field-eyebrow">For the Field</div>
          <h2 className="mt-2 font-outfit text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="mp-field-headline">
            Your techs' favorite tool on the truck
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="mp-field-subheadline">
            A rugged mobile app for work orders, checklists, photos, and GPS check-in — and a big button to collect payment before they pull out of the driveway.
          </p>
          <ul className="mt-7 space-y-3">
            {checklist.map((c) => (
              <li key={c} className="flex items-center gap-3 text-[15px] text-slate-700" data-testid={`mp-field-check-${c.split(" ")[0].toLowerCase()}`}>
                <CheckCircle2 className="h-4.5 w-4.5 shrink-0 text-amber-500" />
                {c}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative order-1 lg:order-2">
          <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/60">
            <img src={IMAGES.field} alt="Field technician holding a tablet in an industrial workshop" className="h-[360px] w-full object-cover sm:h-[420px]" data-testid="mp-field-image" />
          </div>
          <div className="absolute -bottom-6 -left-4 sm:-left-8">
            <PhoneMock />
          </div>
        </div>
      </div>
    </section>
  );
}

function PlatformGrid() {
  const features = [
    { icon: Calendar, title: "Dispatch & Scheduling", desc: "Drag-and-drop dispatch board and a live calendar that keeps every truck moving and every slot full." },
    { icon: Navigation, title: "GPS Tracking for Field Techs", desc: "See every technician live on the map, with geo-aware arrival logs and accurate ETAs sent to customers.", highlight: true },
    { icon: FileText, title: "Estimates & Invoices", desc: "Build estimates in seconds, convert to invoices, and collect with online pay links — no chasing checks." },
    { icon: Users, title: "CRM & Customer Profiles", desc: "Every customer's jobs, equipment, history, and memberships in one organized record." },
    { icon: PhoneCall, title: "AI Voice Agent", desc: "Automated follow-up calls that text or email the invoice with a pay link and book the next job." },
    { icon: Sparkles, title: "Diagnostic Guru", desc: "AI diagnostic co-pilot trained on your service manuals — walks techs through error codes, symptoms, and flow charts in real time." },
    { icon: Mic, title: "Guru Assistant", desc: "Voice command your back-office — draft an estimate, book a job, or message a tech hands-free while you drive." },
    { icon: ShieldCheck, title: "Membership Program", desc: "Sell recurring plans, auto-apply member pricing, and renew on payment for predictable revenue." },
    { icon: Wallet, title: "Payments & Payroll", desc: "Take card payments in the field, track timeclock hours, and run payroll from one platform." },
  ];
  return (
    <section className="bg-white py-24 sm:py-32" data-testid="mp-platform-grid-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <div className="text-[12px] font-bold uppercase tracking-wide text-blue-600" data-testid="mp-platform-eyebrow">Everything In One Platform</div>
          <h2 className="mt-2 font-outfit text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="mp-platform-headline">
            One system for every job, start to finish
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
            Stop stitching together six different apps. ServiceGuru Pro runs your office, your dispatch board, your field crews, and your AI sales agent.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => (
            <div
              key={f.title}
              className={`rounded-2xl border p-6 transition-shadow hover:shadow-lg ${f.highlight ? "border-amber-400 shadow-md shadow-amber-100" : "border-slate-200"}`}
              data-testid={`mp-platform-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${f.highlight ? "bg-amber-50 text-amber-600" : "bg-blue-50 text-blue-600"}`}>
                <f.icon className="h-5 w-5" />
              </span>
              <div className="mt-4 text-[15px] font-bold text-slate-900">{f.title}</div>
              <div className="mt-1.5 text-[13.5px] leading-relaxed text-slate-500">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SizePanel({ icon: Icon, iconCount, title, desc }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-7" data-testid={`mp-size-panel-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="mb-6 flex items-center gap-1.5">
        {Array.from({ length: iconCount }).map((_, i) => (
          <span key={i} className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/15">
            <Icon className="h-4.5 w-4.5 text-blue-400" />
          </span>
        ))}
      </div>
      <div className="text-[17px] font-bold text-white">{title}</div>
      <div className="mt-1.5 text-[13.5px] leading-relaxed text-slate-400">{desc}</div>
    </div>
  );
}

function SizeSection() {
  return (
    <section className="bg-slate-950 py-24 sm:py-32" data-testid="mp-size-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto mb-14 max-w-2xl text-center">
          <h2 className="font-outfit text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="mp-size-headline">
            From Solo Operators to Growing Teams.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-400 sm:text-lg">
            ServiceGuru Pro scales from one tech to twenty without adding headcount — the same AI assistant keeps every size of operation moving.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <SizePanel icon={User} iconCount={1} title="Solo Operator" desc="One tech, running the whole business from the truck." />
          <SizePanel icon={UsersRound} iconCount={3} title="Small Team" desc="A few techs, one shared schedule, zero double-booking." />
          <SizePanel icon={Building2} iconCount={4} title="Larger Dispatch" desc="Full dispatch board, AI handling the overflow calls." />
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = [
    { icon: ShieldCheck, text: "Built for field service businesses" },
    { icon: CreditCard, text: "Stripe-secured payments" },
    { icon: Bot, text: "AI-powered, human-controlled" },
    { icon: MapPin, text: "Florida-based, US support" },
  ];
  return (
    <section className="border-y border-white/10 bg-slate-900/60 py-8" data-testid="mp-trust-bar">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-4 px-6 lg:px-8">
        {items.map((it) => (
          <span key={it.text} className="flex items-center gap-2 text-[13px] font-semibold text-slate-400" data-testid={`mp-trust-item-${it.text.split(" ")[0].toLowerCase()}`}>
            <it.icon className="h-4 w-4 text-blue-400" /> {it.text}
          </span>
        ))}
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative overflow-hidden py-28 sm:py-36" data-testid="mp-final-cta-section">
      <img src={IMAGES.finalCta} alt="Aerial view of a residential neighborhood at golden hour with a service van on the street" className="absolute inset-0 h-full w-full object-cover" data-testid="mp-final-cta-image" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/85 to-slate-950/60" />
      <div className="relative mx-auto max-w-3xl px-6 text-center lg:px-8">
        <h2 className="font-outfit text-3xl font-black tracking-tight text-white sm:text-5xl" data-testid="mp-final-cta-headline">
          Stop Juggling. Start Growing.
        </h2>
        <p className="mt-5 text-base text-slate-300 sm:text-lg" data-testid="mp-final-cta-subheadline">
          Get a 14 day free trial when you sign up today. No credit card required.
        </p>
        <button data-testid="mp-final-cta-btn" className="mt-9 inline-flex items-center gap-2 rounded-full bg-blue-600 px-9 py-4 text-[16px] font-bold text-white transition-all hover:bg-blue-700 active:scale-95">
          Get Started Free <ArrowRight className="h-4.5 w-4.5" />
        </button>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10 bg-slate-950 py-10" data-testid="mp-footer">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 sm:flex-row lg:px-8">
        <span className="font-outfit text-[15px] font-bold text-white">ServiceGuru <span className="text-blue-400">Pro</span></span>
        <span className="text-[12px] text-slate-500">Design preview build · not connected to production data</span>
      </div>
    </footer>
  );
}

export default function MarketingPreview() {
  useGoogleFonts();
  return (
    <div className="min-h-screen bg-slate-950 font-manrope" data-testid="marketing-preview-page">
      <style>{`.font-outfit{font-family:'Outfit',sans-serif}.font-manrope{font-family:'Manrope',sans-serif}`}</style>
      <PreviewBanner />
      <Nav />
      <Hero />
      <VoiceAssistantSection />
      <GPSTrackingSection />
      <OfficeSection />
      <FieldSection />
      <PlatformGrid />
      <SizeSection />
      <TrustBar />
      <FinalCTA />
      <Footer />
    </div>
  );
}
